import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { MetricCard } from '../components/ui/metric-card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import { apiFetch } from '../utils/api';
import {
  Star,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  AlertTriangle,
  Heart,
  Store,
  AlertCircle,
  Activity,
  Package,
  ArrowLeft,
  Search,
  Clock,
  Brain,
  Lightbulb,
  ThumbsUp,
  Cloud,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RATING_COLORS = {
  1: '#ef4444',
  2: '#f97316',
  3: '#f59e0b',
  4: '#84cc16',
  5: '#2eb4ac'
};

// Expandable text component
const ExpandableText = ({ text, maxLength = 150 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  if (text.length <= maxLength) return <span>{text}</span>;
  
  return (
    <span>
      {expanded ? text : `${text.slice(0, maxLength)}...`}
      <button 
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="ml-1 text-[#8b5cf6] hover:underline text-xs font-medium inline-flex items-center"
      >
        {expanded ? <><ChevronUp className="w-3 h-3" /> Less</> : <><ChevronDown className="w-3 h-3" /> More</>}
      </button>
    </span>
  );
};

// Circular Progress Component
const CircularProgress = ({ value, size = 80, strokeWidth = 8, color = '#8b5cf6' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted/20"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ color }}>{value}%</span>
      </div>
    </div>
  );
};

export default function ProductDashboardPage() {
  const { brandName, platformName, productName } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const [platformIcon, setPlatformIcon] = useState(null);

  useEffect(() => {
    if (brandName && platformName && productName) {
      fetchProductData();
      fetchLogos();
    }
  }, [brandName, platformName, productName]);

  const fetchLogos = async () => {
    try {
      const [bRes, pRes] = await Promise.all([
        apiFetch('/api/brands', { credentials: 'include' }),
        apiFetch('/api/platforms', { credentials: 'include' }),
      ]);
      if (bRes.ok) {
        const bl = await bRes.json();
        const match = bl.find(b => b.name === brandName);
        if (match?.logo_url) setBrandLogo(match.logo_url);
      }
      if (pRes.ok) {
        const pl = await pRes.json();
        const match = pl.find(p => p.name === platformName);
        if (match?.icon_url) setPlatformIcon(match.icon_url);
      }
    } catch (err) { console.error(err); }
  };

  const fetchProductData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/dashboard/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}/product/${encodeURIComponent(productName)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        setData(await response.json());
      } else if (response.status === 404) {
        // No reviews yet — show empty state
        setData({
          brand_name: brandName,
          platform_name: platformName,
          product_name: productName,
          image_url: '',
          total_reviews: 0,
          avg_rating: 0,
          health_score: 0,
          sentiment_distribution: {},
          review_trend: [],
          topics: [],
          keywords: [],
          detected_issues: [],
          ai_summary: null,
        });
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load product data');
      }
    } catch (err) {
      console.error('Error fetching product data:', err);
      setError('Failed to load product data');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to reviews with filter
  const navigateToReviews = (filters) => {
    const params = new URLSearchParams();
    if (filters.keyword) params.set('keyword', filters.keyword);
    if (filters.topic) params.set('topic', filters.topic);
    if (filters.feature) params.set('feature', filters.feature);
    if (filters.highlight) params.set('highlight', filters.highlight);
    if (brandName) params.set('brand', brandName);
    if (platformName) params.set('platform', platformName);
    if (productName) params.set('product', productName);
    if (filters.sentiment) params.set('sentiment', filters.sentiment);
    navigate(`/reviews?${params.toString()}`);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate metrics
  const positiveCount = data?.sentiment_distribution?.positive || 0;
  const negativeCount = data?.sentiment_distribution?.negative || 0;
  const neutralCount = data?.sentiment_distribution?.neutral || 0;
  const totalSentiment = positiveCount + negativeCount + neutralCount;
  const positivePercent = totalSentiment > 0 ? Math.round((positiveCount / totalSentiment) * 100) : 0;
  const negativePercent = totalSentiment > 0 ? Math.round((negativeCount / totalSentiment) * 100) : 0;

  // Clickable Word cloud renderer
  const WordCloud = ({ words, color, sentiment }) => {
    if (!words || words.length === 0) return <p className="text-sm text-muted-foreground text-center py-4">No data</p>;
    const maxValue = Math.max(...words.map(w => w.value));
    return (
      <div className="flex flex-wrap gap-2 justify-center p-2">
        {words.slice(0, 20).map((word) => {
          const size = Math.max(0.75, (word.value / maxValue) * 1.4);
          return (
            <button
              key={word.text}
              onClick={() => navigateToReviews({ keyword: word.text, sentiment })}
              className="px-2 py-1 rounded-full transition-all hover:scale-110 hover:shadow-md cursor-pointer"
              style={{
                fontSize: `${size}rem`,
                backgroundColor: `${color}15`,
                color: color,
                fontWeight: word.value > maxValue * 0.5 ? 600 : 400,
              }}
              title={`Click to see ${word.value} reviews mentioning "${word.text}"`}
            >
              {word.text}
              <span className="ml-1 text-xs opacity-60">({word.value})</span>
            </button>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-96" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <AlertCircle className="w-16 h-16 text-destructive" />
        <h2 className="text-xl font-semibold">{error}</h2>
        <Button onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}`)} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Channel Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="product-dashboard-page">
      {/* Header - Product Name on Channel */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}`)} data-testid="back-btn" className="mt-1">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3 flex-wrap">
            <span className="cursor-pointer hover:text-foreground transition-colors" onClick={() => navigate('/dashboard')}>Dashboard</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-foreground transition-colors flex items-center gap-1" onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}`)}>
              {brandLogo && <img src={brandLogo} alt="" className="w-4 h-4 rounded object-cover" />}
              {data?.brand_name || brandName}
            </span>
            <span>/</span>
            <span className="cursor-pointer hover:text-foreground transition-colors flex items-center gap-1" onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}`)}>
              {platformIcon && <img src={platformIcon} alt="" className="w-4 h-4 rounded object-cover" />}
              {data?.platform_name || platformName}
            </span>
          </div>
          
          {/* Product Name on Channel - New Style */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8b5cf6] to-[#2eb4ac] flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden">
              {data?.image_url ? (
                <img src={data.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>'; }} />
              ) : (
                <Package className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" title={data?.product_name}>
                {data?.product_name}
              </h1>
              <p className="text-muted-foreground text-sm flex items-center gap-1">
                on 
                {platformIcon && <img src={platformIcon} alt="" className="w-4 h-4 rounded object-cover" />}
                <span className="font-medium text-foreground">{data?.platform_name}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty state for products with no reviews */}
      {data?.total_reviews === 0 && (
        <Card className="card-hover">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-2">No Customer Reviews Yet</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              This product doesn't have any uploaded text reviews for analysis on {data?.platform_name}.
              Upload reviews via the <button onClick={() => navigate('/upload-reviews')} className="text-[#2eb4ac] hover:underline font-medium">Upload Reviews</button> page to see sentiment analysis, topics, and insights here.
            </p>
          </CardContent>
        </Card>
      )}

      {data?.total_reviews > 0 && (<>
      {/* Row 1: Product Overview KPIs with Info Icons - No Health Score */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          metricKey="total_reviews"
          icon={MessageSquare}
          label="Analysed Reviews"
          value={data?.total_reviews?.toLocaleString()}
          subtext="For this SKU"
        />
        <MetricCard
          metricKey="avg_rating"
          icon={Star}
          iconColor="text-[#f59e0b]"
          label="Avg Rating"
          value={data?.avg_rating}
          suffix="/5"
          subtext="Product average"
        />
        <MetricCard
          metricKey="positive"
          icon={TrendingUp}
          iconColor="text-[#2eb4ac]"
          label="Positive"
          value={positivePercent}
          suffix="%"
          subtext={`${positiveCount} reviews`}
          onClick={() => navigateToReviews({ sentiment: 'positive' })}
          highlight={true}
          highlightColor="#2eb4ac"
        />
        <MetricCard
          metricKey="negative"
          icon={TrendingDown}
          iconColor="text-destructive"
          label="Negative"
          value={negativePercent}
          suffix="%"
          subtext={`${negativeCount} reviews`}
          onClick={() => navigateToReviews({ sentiment: 'negative' })}
        />
        <Card className="card-hover" data-testid="kpi-platform">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              {platformIcon ? (
                <img src={platformIcon} alt="" className="w-4 h-4 rounded object-cover" />
              ) : (
                <Store className="w-4 h-4 text-[#3b82f6]" />
              )}
              <span className="text-xs font-medium">Platform</span>
            </div>
            <p className="text-lg font-bold font-heading truncate">{data?.platform_name}</p>
            <p className="text-xs text-muted-foreground">Marketplace</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Summary - 1-2 liner review summary */}
      {data?.ai_summary && (
        <Card className="card-hover bg-gradient-to-r from-[#8b5cf6]/5 via-[#2eb4ac]/5 to-transparent border-l-4 border-l-[#8b5cf6]" data-testid="ai-summary">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-[#8b5cf6]" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-[#8b5cf6] mb-1 uppercase tracking-wide">AI Insight</p>
                <p className="text-sm text-foreground">
                  {data?.total_reviews > 0 
                    ? `${data?.total_reviews?.toLocaleString()} reviews on ${data?.platform_name}. ${positivePercent}% positive (${positiveCount}), ${negativePercent}% negative (${negativeCount}). Avg rating ${data?.avg_rating}/5. Top topic: "${data?.top_topics?.[0]?.topic || 'quality'}" (${data?.top_topics?.[0]?.count || 0} mentions).`
                    : 'Upload reviews to get AI-powered product insights.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 2: Rating Distribution & Sentiment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rating Distribution */}
        <Card className="card-hover" data-testid="rating-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Star className="w-4 h-4 text-[#f59e0b]" />
              ⭐ Rating Distribution (Product)
            </CardTitle>
            <CardDescription>Breakdown of 1★–5★ reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              {data?.rating_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.rating_distribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis type="category" dataKey="rating" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${v}★`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Reviews" radius={[0, 4, 4, 0]}>
                      {data.rating_distribution.map((entry) => (
                        <Cell key={entry.rating} fill={RATING_COLORS[entry.rating] || '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No rating data</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Platform Performance */}
        <Card className="card-hover" data-testid="platform-performance">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Store className="w-4 h-4 text-[#3b82f6]" />
              🏪 Platform Performance (Product)
            </CardTitle>
            <CardDescription>This SKU across all marketplaces</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              {data?.platform_performance?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.platform_performance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="platform" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 5]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="total_reviews" name="Reviews" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="avg_rating" name="Rating" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No platform data</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Feature Sentiment Analysis with Circular Progress */}
      <Card className="card-hover" data-testid="feature-sentiment">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#8b5cf6]" />
            📊 Feature Sentiment Analysis
          </CardTitle>
          <CardDescription>Click on any feature to see contributing reviews</CardDescription>
        </CardHeader>
        <CardContent>
          {data?.feature_sentiment?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {data.feature_sentiment.map((feature) => (
                <button
                  key={feature.feature}
                  onClick={() => navigateToReviews({ feature: feature.feature.toLowerCase() })}
                  className="flex flex-col items-center p-4 rounded-xl bg-accent/20 hover:bg-accent/40 transition-all cursor-pointer group"
                >
                  <CircularProgress 
                    value={Math.round(feature.sentiment_score)} 
                    size={80} 
                    strokeWidth={8}
                    color={feature.sentiment_score >= 70 ? '#2eb4ac' : feature.sentiment_score >= 50 ? '#f59e0b' : '#ef4444'}
                  />
                  <p className="text-sm font-semibold mt-3 group-hover:text-[#8b5cf6] transition-colors">{feature.feature}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="text-[#2eb4ac]">+{feature.positive}</span>
                    <span className="text-destructive">-{feature.negative}</span>
                  </div>
                  <span className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to view reviews →
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No feature data available</p>
          )}
        </CardContent>
      </Card>

      {/* Row 5: Topics - Positive & Negative (Clickable) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Positive Topics */}
        <Card className="card-hover" data-testid="positive-topics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#2eb4ac]" />
              ❤️ Top Positive Topics (Product)
            </CardTitle>
            <CardDescription>Click to see reviews for each topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.positive_topics?.length > 0 ? (
                data.positive_topics.map((item) => (
                  <button
                    key={item.topic}
                    onClick={() => navigateToReviews({ topic: item.topic, sentiment: 'positive' })}
                    className="px-3 py-1.5 rounded-full bg-[#2eb4ac]/10 text-[#2eb4ac] text-sm font-medium hover:bg-[#2eb4ac]/20 hover:scale-105 transition-all cursor-pointer"
                  >
                    {item.topic} <span className="opacity-60">({item.count})</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No positive topics found</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Negative Topics */}
        <Card className="card-hover" data-testid="negative-topics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              ⚠️ Top Negative Topics (Product)
            </CardTitle>
            <CardDescription>Click to see reviews for each topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.negative_topics?.length > 0 ? (
                data.negative_topics.map((item) => (
                  <button
                    key={item.topic}
                    onClick={() => navigateToReviews({ topic: item.topic, sentiment: 'negative' })}
                    className="px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 hover:scale-105 transition-all cursor-pointer"
                  >
                    {item.topic} <span className="opacity-60">({item.count})</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No negative topics found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 6: Word Clouds (Clickable) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Positive Word Cloud */}
        <Card className="card-hover" data-testid="positive-wordcloud">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Cloud className="w-4 h-4 text-[#2eb4ac]" />
              ☁️ Positive Keyword Cloud
            </CardTitle>
            <CardDescription>Click any keyword to see matching reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="min-h-[150px]">
              <WordCloud words={data?.positive_wordcloud} color="#2eb4ac" sentiment="positive" />
            </div>
          </CardContent>
        </Card>

        {/* Negative Word Cloud */}
        <Card className="card-hover" data-testid="negative-wordcloud">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Cloud className="w-4 h-4 text-destructive" />
              ☁️ Negative Keyword Cloud
            </CardTitle>
            <CardDescription>Click any keyword to see matching reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="min-h-[150px]">
              <WordCloud words={data?.negative_wordcloud} color="#ef4444" sentiment="negative" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 7: Emerging Complaints & Suggestions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Emerging Complaint Trends */}
        <Card className="card-hover" data-testid="emerging-complaints">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Search className="w-4 h-4 text-destructive" />
              🚨 Emerging Complaint Trends
            </CardTitle>
            <CardDescription>Issues increasing for this SKU</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.emerging_complaints?.length > 0 ? (
                data.emerging_complaints.map((item) => (
                  <button
                    key={item.issue}
                    onClick={() => navigateToReviews({ highlight: item.issue, sentiment: 'negative' })}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-all cursor-pointer text-left"
                  >
                    <span className="text-sm font-medium capitalize">{item.issue}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{item.recent} recent</Badge>
                      <span className={`text-xs font-semibold ${item.change > 0 ? 'text-destructive' : 'text-[#2eb4ac]'}`}>
                        {item.change > 0 ? `↑${item.change}` : item.change === 0 ? '→' : `↓${Math.abs(item.change)}`}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No emerging trends detected 🎉</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Suggestions */}
        <Card className="card-hover" data-testid="customer-suggestions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-[#f59e0b]" />
              💡 Customer Suggestions
            </CardTitle>
            <CardDescription>Improvement requests from reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[180px]">
              <div className="space-y-3 pr-2">
                {data?.customer_suggestions?.length > 0 ? (
                  data.customer_suggestions.map((suggestion, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-[#f59e0b]/5 border-l-2 border-[#f59e0b]">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < suggestion.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <ExpandableText text={suggestion.review_text} maxLength={120} />
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No suggestions found</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Row 8: Recent Reviews */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Critical Reviews */}
        <Card className="card-hover" data-testid="critical-reviews">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Clock className="w-4 h-4 text-destructive" />
              📝 Recent Critical Reviews (1★-2★)
            </CardTitle>
            <CardDescription>Latest low-rating reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[220px]">
              <div className="space-y-3 pr-2">
                {data?.critical_reviews?.length > 0 ? (
                  data.critical_reviews.map((review) => (
                    <div key={review.review_id} className="p-3 rounded-lg bg-destructive/5 border-l-4 border-destructive">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <ExpandableText text={review.review_text} maxLength={150} />
                      </p>
                      {review.detected_issues?.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {review.detected_issues.slice(0, 3).map((issue) => (
                            <button
                              key={issue}
                              onClick={() => navigateToReviews({ highlight: issue })}
                              className="px-2 py-0.5 rounded text-xs bg-destructive/10 text-destructive hover:bg-destructive/20 cursor-pointer"
                            >
                              {issue}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No critical reviews - Great! 🎉</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Positive Reviews */}
        <Card className="card-hover" data-testid="positive-reviews">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 text-[#2eb4ac]" />
              🌟 Recent Positive Reviews (4★-5★)
            </CardTitle>
            <CardDescription>Latest high-rating reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[220px]">
              <div className="space-y-3 pr-2">
                {data?.positive_reviews?.length > 0 ? (
                  data.positive_reviews.map((review) => (
                    <div key={review.review_id} className="p-3 rounded-lg bg-[#2eb4ac]/5 border-l-4 border-[#2eb4ac]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <ExpandableText text={review.review_text} maxLength={150} />
                      </p>
                      {review.topics?.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {review.topics.slice(0, 3).map((topic) => (
                            <button
                              key={topic}
                              onClick={() => navigateToReviews({ topic, sentiment: 'positive' })}
                              className="px-2 py-0.5 rounded text-xs bg-[#2eb4ac]/10 text-[#2eb4ac] hover:bg-[#2eb4ac]/20 cursor-pointer"
                            >
                              {topic}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No recent positive reviews</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
      </>)}
    </div>
  );
}
